import { useCallback, useEffect, useState } from 'react';
import ActionButton from '@/components/Button/ActionButton';
import ImageDialog from '@/components/Dialog/ImageDialog/ImageDialog';
import Icon from '@/components/Icon/Icon';
import { HStack } from '@/components/Layout';
import { Text } from '@/components/Typography';
import type { ImageType } from '@/features/section-attribute/types';
import { defaultNS, useTranslation } from '@/lib/i18n';
import { CFormItemBase, type CFormItemProps, buildCFormItem } from '../CFormItem';

// 画像タイプのマッピング定数
const IMAGE_TYPE_MAP = {
  qr_code: {
    icon: 'QrCodeIcon' as const
  },
  barcode: {
    icon: 'ScanBarcodeIcon' as const
  },
  photo: {
    icon: 'ImageIcon' as const
  },
  document: {
    icon: 'FileTextIcon' as const
  }
} as const satisfies Record<ImageType, {
  readonly icon: string;
}>;
type ImageData = {
  id: string;
  type: ImageType | null;
  imageUrl?: string;
  isGenerated: boolean;
};
export type CFormImageItemProps = CFormItemProps & {
  /**
   * 画像データ
   */
  image: ImageData | null;
  /**
   * 画像表示時のコールバック関数
   */
  onViewImage?: (imageId: string) => void;
  /**
   * 画像生成時のコールバック関数
   */
  onGenerateImage?: (itemId: string) => void;
  /**
   * 画像生成・発行パッケージへの遷移URL
   */
  onNavigateToIssuePackage?: () => void;
  /**
   * 未生成時のテキスト（デフォルト: "-"）
   */
  notGeneratedText?: string;
  /**
   * 生成ボタンのテキスト（デフォルト: "Issue new"）
   */
  generateButtonText?: string;
  /**
   * 再生成ボタンのテキスト（デフォルト: "Re-issue"）
   * TODO: すでに値がある状態の時に別アクションを取りたい時の実装
   */
  regenerateButtonText?: string;
};
export const CFormImageItem = buildCFormItem((props: CFormImageItemProps) => {
  const {
    t
  } = useTranslation(defaultNS, {
    keyPrefix: 'pages.devices.profile.sections.base'
  });
  const {
    image,
    onViewImage,
    onGenerateImage,
    onNavigateToIssuePackage,
    notGeneratedText = '-',
    generateButtonText = t('generateLabel'),
    //    regenerateButtonText = t("regenerateLabel"),
    ...baseProps
  } = props;

  // 画像の読み込み状態を管理
  const [imageLoadError, setImageLoadError] = useState(false);
  // onViewImage 未指定時に使う内蔵プレビューダイアログの状態
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);

  // 画像URLが変更されたときにエラー状態をリセット
  useEffect(() => {
    if (image?.imageUrl) {
      setImageLoadError(false);

      // 画像の存在を確認
      const img = new Image();
      const handleLoad = () => {
        setImageLoadError(false);
      };
      const handleError = () => {
        setImageLoadError(true);
      };
      img.onload = handleLoad;
      img.onerror = handleError;
      img.src = image.imageUrl;
      return () => {
        img.onload = null;
        img.onerror = null;
      };
    } else {
      setImageLoadError(false);
    }
  }, [image?.imageUrl]);
  const handleViewClick = useCallback(() => {
    if (image?.id && onViewImage) {
      onViewImage(image.id);
    } else {
      setIsPreviewOpen(true);
    }
  }, [image?.id, onViewImage]);
  const handleGenerateClick = useCallback(() => {
    if (onNavigateToIssuePackage) {
      // 型安全なナビゲーションコールバックが指定されている場合は実行
      onNavigateToIssuePackage();
    } else if (onGenerateImage) {
      // コールバック関数が指定されている場合は実行
      onGenerateImage(baseProps.id);
    }
  }, [onNavigateToIssuePackage, onGenerateImage, baseProps.id]);

  // 画像が生成されていない場合の表示
  const renderNotGenerated = () => {
    // 編集モードでない場合はシンプルに"-"のみ表示
    if (!baseProps.isEditing) {
      return <HStack className="items-center py-2">
          <Text variant="labelReg" className="text-neutral-muted-on-fill">
            {notGeneratedText}
          </Text>
        </HStack>;
    }

    // 編集モードの場合は生成ボタンを表示
    return <HStack className="items-center gap-3 py-2">
        <Text variant="labelReg" className="text-neutral-muted-on-fill">
          {notGeneratedText}
        </Text>
        <ActionButton variant="outline" size="sm" onClick={handleGenerateClick} className="ml-auto">
          <HStack className="items-center gap-2">
            <Icon iconName="PlusIcon" className="w-4 h-4" />
            <Text variant="buttonLabelSmall">{generateButtonText}</Text>
          </HStack>
        </ActionButton>
      </HStack>;
  };

  // 画像が生成されている場合の表示
  const renderGenerated = () => {
    const imageTypeInfo = image?.type ? IMAGE_TYPE_MAP[image.type] : null;
    return <HStack className="items-center gap-3 py-2">
        <HStack className="items-center gap-2">
          <ActionButton variant="outline" size="sm" onClick={handleViewClick} className="w-8 h-8 p-0">
            <Icon iconName={imageTypeInfo?.icon ?? 'ImageIcon'} className="w-4 h-4" />
          </ActionButton>
        </HStack>
      </HStack>;
  };

  // 画像が実際に利用可能かを判定
  const isImageActuallyAvailable = image && image.isGenerated && !imageLoadError;
  return <>
      <CFormItemBase {...baseProps} readComponent={isImageActuallyAvailable ? renderGenerated() : renderNotGenerated()} writeComponent={isImageActuallyAvailable ? renderGenerated() : renderNotGenerated()} />
      {!onViewImage && image?.imageUrl && <ImageDialog title={baseProps.label} image={{
      url: image.imageUrl,
      alt: typeof baseProps.label === 'string' ? baseProps.label : ''
    }} open={isPreviewOpen} onOpenChange={setIsPreviewOpen} contentClassName="w-[560px] p-6" imageClassName="max-w-[300px] max-h-[300px] object-contain border border-neutral-border rounded p-3" />}
    </>;
});